// KanbanBoard.jsx
import React from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { User, CalendarDays, CalendarCheck, Network } from "lucide-react";
import {
  LabelChip,
  PriorityBadge,
  PointsPill,
} from "../Common/EntityWorkspace";
import styles from "./KanbanBoard.module.scss";

const KanbanBoard = ({
  columnsMeta = [],
  items = [],
  setItems,
  onItemClick,
}) => {
  const grouped = columnsMeta.map((c) => ({
    ...c,
    items: items.filter(
      (i) => (i.status || "").toLowerCase() === c.status.toLowerCase()
    ),
  }));

  const handleDragEnd = (result) => {
    const { source, destination, draggableId } = result || {};
    if (!destination) return;
    if (
      source?.droppableId === destination?.droppableId &&
      source?.index === destination?.index
    )
      return;

    setItems((prev) =>
      prev.map((i) =>
        i.id === draggableId ? { ...i, status: destination.droppableId } : i
      )
    );
  };

  return (
    <div className={styles.board}>
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className={styles.columns}>
          {grouped.map((col) => (
            <Droppable droppableId={col.status} key={col.status}>
              {(drop) => (
                <div
                  ref={drop.innerRef}
                  {...drop.droppableProps}
                  className={styles.column}
                >
                  {/* Kolon Başlığı */}
                  <div className={styles.columnHead}>
                    <div className={styles.columnTitle}>
                      <span>{col.title}</span>
                      <span className={styles.columnCount}>
                        {col.items.length}
                      </span>
                    </div>
                    <div
                      className={styles.columnUnderline}
                      style={{ "--kb-underline": col.underline }}
                    />
                  </div>

                  {/* Kartlar */}
                  {col.items.map((task, index) => (
                    <Draggable
                      key={task.id}
                      draggableId={task.id}
                      index={index}
                    >
                      {(drag) => (
                        <div
                          ref={drag.innerRef}
                          {...drag.draggableProps}
                          {...drag.dragHandleProps}
                          onClick={() => onItemClick && onItemClick(task)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onItemClick && onItemClick(task);
                            }
                          }}
                          className={styles.card}
                          style={drag.draggableProps.style}
                        >
                          {/* Üst satır: avatar + ID + title */}
                          <div className={styles.cardTop}>
                            <div className={styles.avatar}>
                              <User size={16} />
                            </div>
                            <div className={styles.cardTitle}>
                              {task.id}: {task.title}
                            </div>
                          </div>

                          {/* Tarihler */}
                          <div className={styles.meta}>
                            <div className={styles.metaItem}>
                              <CalendarDays size={12} />
                              {task.startDate}
                            </div>
                            <div className={styles.metaItem}>
                              <CalendarCheck size={12} />
                              {task.deadline}
                            </div>
                          </div>

                          {/* Proje adı */}
                          <div className={styles.metaLine}>
                            <Network size={12} />
                            {task.relatedTo}
                          </div>

                          {/* Atanan kişi */}
                          <div className={styles.metaLine}>
                            <User size={12} />
                            {task.assignedTo}
                          </div>

                          {/* Etiket satırı */}
                          <div className={styles.labels}>
                            {Array.isArray(task.labels) &&
                              task.labels.map((lb, i) => (
                                <LabelChip key={i}>{lb}</LabelChip>
                              ))}
                            <PriorityBadge level={task.priority} />
                            <PointsPill value={task.points} />
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}

                  {drop.placeholder}
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>
    </div>
  );
};

export default KanbanBoard;
